import { NgClass } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
  Inject,
  ViewChild,
  ElementRef,
  Renderer2,
  DOCUMENT,
  inject,
  DestroyRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SessionService } from '../../../../core/services/session/session.service';
import { StorageService } from '../../../../core/services/storage/storage.service';
import { BREAK_POINTS } from '../../../../shared/constants/break-points.constant';
import { Role, ROLES } from '../../../../shared/enums/role.enum';
import { ScreenType } from '../../../../shared/enums/screen-type.enum';
import { NavItemI } from '../../../../shared/interfaces/nav-item.interface';
import { UserI } from '../../../../shared/interfaces/user.interface';
import { DROP_DOWN_ITEMS } from './constants/drop-down-items.constant';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header-logged-in-user',
  imports: [TranslateModule, NgClass, RouterLink],
  templateUrl: './header-logged-in-user.component.html',
  styleUrl: './header-logged-in-user.component.scss',
})
export class HeaderLoggedInUserComponent implements OnInit, AfterViewChecked {
  @ViewChild('dropdownWrapper', { static: false }) dropdownWrapper!: ElementRef;
  private readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly cd = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  constructor(@Inject(DOCUMENT) private document: Document) {}
  isMobileScreen!: boolean;
  isTabletScreen!: boolean;
  loggedInUser: UserI | null = null;
  roles = ROLES;
  isDropDownOpen = false;
  dropDownItems = DROP_DOWN_ITEMS;
  currentUrl: string = this.router.url.slice(1);
  role!: Role;

  ngAfterViewChecked(): void {
    this.initLoggedInUser();
    this.cd.detectChanges();
  }

  ngOnInit(): void {
    this.initScreenTypeListener();
    this.listenToRouteEvents();
    this.role = Math.min(...this.sessionService.getDecodedToken().roles);
    this.focusListener();
  }
  focusListener() {
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      if (
        this.isDropDownOpen &&
        this.dropdownWrapper &&
        !this.dropdownWrapper.nativeElement.contains(event.target)
      ) {
        this.isDropDownOpen = false;
        this.cd.detectChanges();
      }
    });
  }

  initLoggedInUser() {
    this.sessionService
      .getLoggedInUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (loggedInUser) => {
          this.loggedInUser = loggedInUser;
        },
      });
  }

  initScreenTypeListener() {
    if (this.document.defaultView!.innerWidth < BREAK_POINTS.mobile) {
      this.isMobileScreen = true;
      this.isTabletScreen = false;
    } else if (
      this.document.defaultView!.innerWidth <
      BREAK_POINTS.tablet + 9
    ) {
      this.isMobileScreen = false;
      this.isTabletScreen = true;
    }
    this.sessionService
      .getScreenType$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (screenType) => {
          this.isMobileScreen = screenType === ScreenType.MOBILE;
          this.isTabletScreen = screenType === ScreenType.TABLET;
        },
      });
  }

  public getDropDownItemIcon(dropDownItem: NavItemI) {
    return dropDownItem.route[0] === this.currentUrl! ||
      (this.currentUrl === undefined && dropDownItem.alt === 'Home')
      ? dropDownItem.activeIcon
      : dropDownItem.nonActiveIcon;
  }

  listenToRouteEvents(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.url.slice(1);
        }
      });
  }

  public isCurrentRouteActive(dropDownItem: NavItemI) {
    return dropDownItem.route[0] === this.currentUrl!;
  }

  openCloseDropDown() {
    this.isDropDownOpen = !this.isDropDownOpen;
  }

  isRtl() {
    return this.storageService.language === 'ar';
  }

  extractInitials(name: string) {
    name = (name ?? 'guest user').trim().toUpperCase();
    return name[0] + ' ' + name[name.lastIndexOf(' ') + 1];
  }
}
